import localforage from 'localforage';
import moment from 'moment';

const orderBackupStore = localforage.createInstance({
  name: 'OrderBackup',
  storeName: 'orders',
});

const orderStatsStore = localforage.createInstance({
  name: 'OrderStats',
});

const updateStatsCache = async (incrementToday = false, incrementPending = false, decrementPending = false) => {
  try {
    const today = moment().format('YYYY-MM-DD');
    const cached = await orderStatsStore.getItem(today) || { todayOrdersCount: 0, pendingOrdersCount: 0 };

    let newPending = cached.pendingOrdersCount;
    if (incrementPending) newPending += 1;
    if (decrementPending) newPending = Math.max(0, newPending - 1);

    const updated = {
      todayOrdersCount: cached.todayOrdersCount + (incrementToday ? 1 : 0),
      pendingOrdersCount: newPending,
      lastUpdated: new Date().toISOString(),
    };

    await orderStatsStore.setItem(today, updated);
  } catch (error) {
    console.error('Error updating order stats cache:', error);
  }
};

export const ORDER_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  FAILED: 'failed',
  RETRYING: 'retrying',
  CANCELLED: 'cancelled'
};

export const backupOrder = async (orderData) => {
  try {
    const backupId = `backup_${new Date().valueOf()}_${orderData.id || 'unknown'}`;
    const backupOrder = {
      id: backupId,
      originalOrderId: orderData.id,
      operationId: orderData.operationId,
      customer: orderData.customer,
      orderType: orderData.orderType,
      variables: orderData.variables,
      mobileCreatedAt:
        orderData.mobileCreatedAt ||
        orderData.variables?.input?.mobileCreatedAt ||
        null,
      items: orderData.items || orderData.variables?.input?.items || [],
      total: orderData.total || orderData.variables?.input?.total || 0,
      comment: orderData.comment || orderData.variables?.input?.comment || '',
      status: orderData.offline ? ORDER_STATUS.PENDING : ORDER_STATUS.SENT,
      createdAt: new Date().toISOString(),
      lastAttempt: new Date().toISOString(),
      attemptCount: orderData.offline ? 0 : 1,
      isLocalBackup: true,
      koImages: orderData.koImages || null,
      operationType: orderData.operationType || 'INSERT_ORDER',
      needsPricingReview: Boolean(orderData.needsPricingReview),
      usesGrossPrices: Boolean(orderData.usesGrossPrices),
      gpsSuccess: orderData.gpsSuccess || false,
    };

    const existingOrders = await getAllBackupOrders();
    const updatedOrders = [...existingOrders, backupOrder];

    await orderBackupStore.setItem('backupOrders', updatedOrders);

    if (backupOrder.status === ORDER_STATUS.PENDING) {
      await updateStatsCache(false, true, false);
    } else if (backupOrder.status === ORDER_STATUS.SENT) {
      await updateStatsCache(true, false, false);
    }

    return backupId;
  } catch (error) {
    console.error('Error backing up order:', error);
    return null;
  }
};

export const updateBackupOrderPricing = async (operationId, variables, items, total) => {
  const orders = (await orderBackupStore.getItem('backupOrders')) || [];
  await orderBackupStore.setItem('backupOrders', orders.map((order) =>
    order.operationId === operationId
      ? { ...order, variables, items, total, needsPricingReview: false }
      : order));
};

const RESOLVED_STATUSES = new Set([ORDER_STATUS.SENT, ORDER_STATUS.CANCELLED]);

export const getAllBackupOrders = async () => {
  try {
    const orders = await orderBackupStore.getItem('backupOrders') || [];

    const sevenDaysAgo = moment().subtract(7, 'days').toISOString();
    const validOrders = orders.filter(order =>
      !RESOLVED_STATUSES.has(order.status) ||
      moment(order.createdAt).isAfter(sevenDaysAgo)
    );

    if (validOrders.length !== orders.length) {
      await orderBackupStore.setItem('backupOrders', validOrders);
    }

    return validOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch (error) {
    console.error('Error getting backup orders:', error);
    return [];
  }
};

export const getBackupOrdersByDate = async (date) => {
  try {
    const allOrders = await getAllBackupOrders();
    const targetDate = moment(date).format('YYYY-MM-DD');
    
    return allOrders.filter(order => 
      moment(order.createdAt).format('YYYY-MM-DD') === targetDate
    );
  } catch (error) {
    console.error('Error getting backup orders by date:', error);
    return [];
  }
};

export const updateOrderStatus = async (orderId, status, additionalData = {}) => {
  try {
    const orders = await getAllBackupOrders();
    const orderIndex = orders.findIndex(order =>
      order.id === orderId || order.operationId === orderId || order.originalOrderId === orderId
    );

    if (orderIndex === -1) {
      console.warn(`Order not found for status update: ${orderId}`);
      return false;
    }

    orders[orderIndex] = {
      ...orders[orderIndex],
      status,
      lastAttempt: new Date().toISOString(),
      attemptCount: status === ORDER_STATUS.RETRYING ?
        (orders[orderIndex].attemptCount || 0) + 1 : orders[orderIndex].attemptCount,
      ...additionalData
    };

    await orderBackupStore.setItem('backupOrders', orders);
    return true;
  } catch (error) {
    console.error('Error updating order status:', error);
    return false;
  }
};

export const removeBackupOrder = async (orderId) => {
  try {
    const orders = await getAllBackupOrders();
    const filteredOrders = orders.filter(order =>
      order.id !== orderId &&
      order.operationId !== orderId &&
      order.originalOrderId !== orderId
    );

    await orderBackupStore.setItem('backupOrders', filteredOrders);
    return true;
  } catch (error) {
    console.error('Error removing backup order:', error);
    return false;
  }
};

export const markOrderAsSent = async (orderId, serverOrderId) => {
  const result = await updateOrderStatus(orderId, ORDER_STATUS.SENT, {
    serverOrderId,
    sentAt: new Date().toISOString()
  });
  if (result) {
    await updateStatsCache(true, false, true);
  }
  return result;
};

export const markOrderAsFailed = async (orderId, error) => {
  return await updateOrderStatus(orderId, ORDER_STATUS.FAILED, {
    lastError: error?.message || 'Unknown error',
    failedAt: new Date().toISOString()
  });
};

export const markOrderAsCancelled = async (orderId) => {
  const result = await updateOrderStatus(orderId, ORDER_STATUS.CANCELLED, {
    cancelledAt: new Date().toISOString()
  });
  if (result) {
    await updateStatsCache(false, false, true);
  }
  return result;
};


export const cleanupExpiredOrders = async () => {
  try {
    await getAllBackupOrders(); // This will automatically clean up expired orders
  } catch (error) {
    console.error('Error during cleanup:', error);
  }
};

export const cleanupSentOrders = async () => {
  try {
    const orders = await getAllBackupOrders();
    const activeOrders = orders.filter(order =>
      order.status !== ORDER_STATUS.SENT
    );

    if (activeOrders.length !== orders.length) {
      await orderBackupStore.setItem('backupOrders', activeOrders);
    }

    return activeOrders;
  } catch (error) {
    console.error('Error cleaning up sent orders:', error);
    return [];
  }
};

// Verify if order exists on server (to be implemented with actual GraphQL query)
export const verifyOrderOnServer = async (orderId, apolloClient) => {
  try {
    // This would need to be implemented with actual GraphQL query
    // For now, returning false to keep local orders
    return false;
  } catch (error) {
    console.error('Error verifying order on server:', error);
    return false;
  }
};
